import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayIcon, XIcon } from 'lucide-react';
import { ScaledViewport } from '../ScaledViewport';
import { SnakStill } from '../SnakStill';
import { LIVE_SNAK_URL } from './PhoneSnak';
interface WebsiteSnakDemoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
const WEBSITE_SCREENSHOT = 'https://cdn.magicpatterns.com/uploads/5QCzqcMsRSFXbK8DdQEmgk/Screenshot_2026-10-22_at_12.36.46.png';

/** Matches the REKOVELLE site's own purple so the invitation feels native. */
const SITE_PURPLE = '#5E4291';

/**
 * The existing REKOVELLE HCP website with a compact Snak invitation.
 * Clicking the invitation embeds the live Snak in a window above the page.
 * Positions are percentages of the screenshot.
 */
export function WebsiteSnakDemo({
  open,
  onOpenChange
}: WebsiteSnakDemoProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);
  return <div className="overflow-hidden rounded-[18px] bg-white shadow-[0_40px_80px_-44px_rgba(14,27,51,0.55)] ring-1 ring-[var(--fg)]/10" data-id="element-666" data-mp="1lga635ini7p6">
      {/* browser chrome */}
      <div className="flex items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-667" data-mp="1ud2br3mx269i">
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-668" data-mp="29al74ou3nhpx">
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-669" data-mp="24d1n18aq0aq2" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-670" data-mp="24ayvgjtx69j8" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-671" data-mp="2i3olczckjylm" />
        </div>
        <span className="truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)]" data-id="element-672" data-mp="2e3w5s2orcs6r">
          REKOVELLE® (follitropin delta) · HCP website
        </span>
      </div>

      <div className="relative overflow-hidden" data-id="element-673" data-mp="2n4ucwtuhtftk">
        <img src={WEBSITE_SCREENSHOT} alt="Existing REKOVELLE HCP website: 'Optimising fertility treatment with potency and precision' hero, with the indication statement below" className="block w-full" aria-hidden={open || undefined} data-id="element-674" data-mp="1kq4wi6t3k1q0" />

        {/* invitation button, placed in the empty space of the indication band */}
        {!open && <button type="button" onClick={() => onOpenChange(true)} aria-label="Open the REKOVELLE teaser Snak" className="group absolute flex items-center gap-[6%] rounded-[12px] bg-white p-[0.7%] text-left shadow-[0_20px_44px_-18px_rgba(14,27,51,0.5)] ring-1 ring-black/5 transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" style={{
        left: '1.6%',
        top: '69%',
        width: '21%',
        height: '14%'
      }} data-id="element-675" data-mp="23gcvccprk60e">
            <motion.span aria-hidden="true" className="absolute inset-0 rounded-[12px] ring-2 ring-[#5E4291]" animate={{
          opacity: [0.9, 0.15, 0.9],
          scale: [1, 1.04, 1]
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} data-id="element-676" data-mp="n1bdqefecvmg" />
            <SnakStill screen={0} alt="" className="h-full w-[24%] shrink-0" data-id="element-677" data-mp="5ttw9zb1jb35" />
            <span className="min-w-0" data-id="element-678" data-mp="u7hhlfkdms8">
              <span className="block text-[clamp(6px,0.7vw,10px)] font-bold uppercase tracking-[0.14em]" style={{
            color: SITE_PURPLE
          }} data-id="element-679" data-mp="vaaq00d01bb">
                New · 40-second case
              </span>
              <span className="mt-[2%] block text-[clamp(8px,1.05vw,14px)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-680" data-mp="oj07705leyee">
                Two patients. Same approach?
              </span>
              <span className="mt-[6%] inline-flex items-center gap-1 rounded-full px-[0.6vw] py-[0.25vw] text-[clamp(6px,0.75vw,11px)] font-bold text-white" style={{
            backgroundColor: SITE_PURPLE
          }} data-id="element-681" data-mp="399ivilwn3nsx">
                <PlayIcon className="h-[0.8em] w-[0.8em] fill-current" aria-hidden="true" data-id="element-682" data-mp="wy681d7xatol" />
                Play the Snak
              </span>
            </span>
            <span className="absolute -top-8 left-0 whitespace-nowrap rounded-full bg-[var(--fg)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white shadow-lg transition-transform group-hover:-translate-y-0.5" data-id="element-683" data-mp="n1bdf5ertdfl">
              Click to play
            </span>
          </button>}

        {/* the live Snak, embedded above the website */}
        <AnimatePresence data-id="element-684" data-mp="1ko0khtobyg8b">
          {open && <>
              <motion.button key="dim" type="button" aria-label="Close the Snak" onClick={() => onOpenChange(false)} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.35
          }} className="absolute inset-0 cursor-default bg-[rgba(20,12,36,0.55)] focus:outline-none" data-id="element-685" data-mp="16nzbfqxww2hr" />
              <motion.div key="snak" role="dialog" aria-label="REKOVELLE teaser Snak embedded above the website" initial={{
            opacity: 0,
            scale: 0.3
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.3
          }} transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1]
          }} className="absolute flex flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)]" style={{
            left: '50%',
            top: '4%',
            height: '92%',
            aspectRatio: '420 / 820',
            x: '-50%',
            transformOrigin: '-60% 80%'
          }} data-id="element-686" data-mp="3iyhj1655m98e">
                <div className="flex shrink-0 items-center justify-between px-3 py-2 text-white" style={{
              backgroundColor: SITE_PURPLE
            }} data-id="element-687" data-mp="3a3ul0v1tkz66">
                  <span className="text-[clamp(8px,0.8vw,11px)] font-bold uppercase tracking-[0.14em]" data-id="element-688" data-mp="2hf5qjev9rs3e">
                    REKOVELLE · 40-second case
                  </span>
                  <button type="button" onClick={() => onOpenChange(false)} aria-label="Close the Snak" className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-689" data-mp="3b5o7iol4gk85">
                    <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-690" data-mp="2s968dm3y1i1v" />
                  </button>
                </div>
                <ScaledViewport baseWidth={390} baseHeight={760} radius={0} label="Live REKOVELLE teaser Snak" className="min-h-0 flex-1 bg-white" data-id="element-691" data-mp="2x5myumym88i4">
                  <iframe src={LIVE_SNAK_URL} title="Live REKOVELLE teaser Snak" width={390} height={760} className="block h-[760px] w-[390px] border-0 bg-white" allow="autoplay; fullscreen" data-id="element-692" data-mp="hfk8xgn3rre2" />
                </ScaledViewport>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
    </div>;
}